import type { ErrorInfo, ReactNode } from 'react';
import React from 'react';
import type { WithTranslation } from 'react-i18next';
import { withTranslation } from 'react-i18next';

import { analytics } from '@/analytics';
import { Button } from '@/components/atoms/Buttons';
import { withSentry } from '@/utils/withSentry';

import styles from './SentryErrorBoundary.module.scss';

interface SentryErrorBoundaryProps extends WithTranslation {
    children?: ReactNode;
}

interface SentryErrorBoundaryState {
    error: Error | null;
    eventId: string | null;
}

class SentryErrorBoundary extends React.Component<
    SentryErrorBoundaryProps,
    SentryErrorBoundaryState
> {
    constructor(props: SentryErrorBoundaryProps) {
        super(props);
        this.state = { error: null, eventId: null };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        this.setState({ error });

        analytics.errorEncountered({ errorType: 'render' });

        withSentry((s) =>
            s.withScope((scope) => {
                scope.setExtras({ ...errorInfo });
                const eventId = s.captureException(error);
                this.setState({ eventId });
            })
        );
    }

    render(): ReactNode {
        if (this.state.error) {
            //render fallback UI
            return (
                <div className={styles.fallback}>
                    <h1>{this.props.t('errorBoundary.title')}</h1>
                    <p>{this.props.t('errorBoundary.description')}</p>
                    <Button
                        variant="primary"
                        onClick={() =>
                            withSentry((s) =>
                                s.showReportDialog({ eventId: this.state.eventId ?? undefined })
                            )
                        }>
                        {this.props.t('errorBoundary.reportFeedback')}
                    </Button>
                </div>
            );
        }
        //when there's not an error, render children untouched
        return this.props.children;
    }
}

export default withTranslation()(SentryErrorBoundary);
